// Mount-local UI state that survives its subtree being unmounted.
//
// The WS attach effect wipes the timeline and replays the full event bus on
// EVERY reconnect (silent Cloud Run 1006 drop, idle resume, reauth — see
// useWsSession). Everything rendered FROM the timeline unmounts for
// the replay window and remounts afterwards, so plain `useState` inside a
// timeline-rendered card (a half-typed answer, a link selection, a one-shot
// "Sent" latch) is destroyed and comes back at its initial value: drafts
// vanish, confirm buttons re-arm. The focus flow has a second remount channel
// with the same effect — FocusFrame's `key={stepKey}` body wrapper replays the
// fade-up animation by remounting the whole step.
//
// `useRemountSafeState` is a drop-in `useState` whose latest value is ALSO
// written through to a keyed store owned ABOVE the remount boundary (the
// session hook for chat surfaces, the flow component for focus). On remount
// the initializer re-seeds from the store, so the state survives any number of
// unmount/remount cycles within the owner's lifetime — and only that lifetime:
// the store dies (or is cleared) with its owner, so a genuine session switch
// still starts clean.
//
// Keys must be stable across the remount (a timeline entry id — stable as
// `${sessionId}#${seq}` across replay — or a field name) and stable for the
// lifetime of one mounted component (the hook reads the store only in the
// initializer). Pass `null` to opt out — the hook degrades to plain useState,
// which also covers surfaces rendered without a provider (tests, storybook).

import {
  createContext,
  useCallback,
  useContext,
  useState,
  type ReactNode,
} from "react";

/** Plain keyed bag. Values are held by reference (Sets/Maps welcome) — this is
 *  in-memory per-tab state, never serialized. */
export type RemountSafeStore = Map<string, unknown>;

export const createRemountSafeStore = (): RemountSafeStore => new Map();

const RemountSafeStoreContext = createContext<RemountSafeStore | null>(null);

export function RemountSafeStoreProvider({
  store,
  children,
}: {
  readonly store: RemountSafeStore;
  readonly children: ReactNode;
}) {
  return (
    <RemountSafeStoreContext.Provider value={store}>
      {children}
    </RemountSafeStoreContext.Provider>
  );
}

/**
 * `useState`, but the latest value is mirrored into the nearest
 * `RemountSafeStoreProvider`'s store under `key`, and the initial value is
 * read back from it on (re)mount. With `key === null` or no provider it is
 * exactly `useState(initial)`.
 *
 * Writing the initial value back (e.g. clearing a draft on submit) is the
 * idiomatic "clear": a later remount then re-seeds with that same empty value,
 * indistinguishable from a fresh mount.
 */
export function useRemountSafeState<T>(
  key: string | null,
  initial: T | (() => T),
): [T, (next: T | ((prev: T) => T)) => void] {
  const store = useContext(RemountSafeStoreContext);
  const [state, setState] = useState<T>(() => {
    if (store !== null && key !== null && store.has(key)) {
      return store.get(key) as T;
    }
    return typeof initial === "function" ? (initial as () => T)() : initial;
  });
  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      if (typeof next === "function") {
        // The store write rides inside the updater so the mirrored value is
        // exactly what React commits. The write is idempotent, so StrictMode's
        // double-invoked updater is harmless.
        setState((prev) => {
          const value = (next as (p: T) => T)(prev);
          if (store !== null && key !== null) store.set(key, value);
          return value;
        });
      } else {
        if (store !== null && key !== null) store.set(key, next);
        setState(next);
      }
    },
    [store, key],
  );
  return [state, set];
}
